/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import ChevronDownSvg from 'images/v5/icons/chevron-down.svg';
import DotsIcon from 'images/v5/icons/dots-vertical.svg';
import EditSvg from 'images/v5/icons/edit-02.svg';
import PlusSvg from 'images/v5/icons/plus.svg';
import TrashSvg from 'images/v5/icons/trash-03.svg';
import Button from 'layout/atoms/Button/Button';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import FileInput from 'layout/atoms/FileInput';
import Header from 'layout/atoms/Header/Header';
import Grid from 'layout/molecules/Grid/Grid';
import VisualEditor from 'layout/organisms/VisualEditor/VisualEditor';
import { openModal, selectModals } from 'store/layoutSlice';
import { fetchSpecificationComponentHeaders, selectSpecificationComponentHeadersRefetch, selectSpecificationComponentHeadersSpecificationId, selectSpecificationComponentHeadersStatus, shouldFetchSpecificationComponentQuery, } from 'store/specificationComponentsSlice';
import { selectSpecificationId } from 'store/specificationDetailsSlice';
import { addSpecificationProjection, fetchSpecificationProjection, fetchSpecificationProjections, removeSpecificationProjection, resetSpecificationProjectionsState, selectSpecificationProjection, selectSpecificationProjections, selectSpecificationProjectionsRefetchFlags, selectSpecificationProjectionsStatus, } from 'store/specificationProjectionsSlice';
import CommentCreator from './CommentCreator/CommentCreator';
import ProjectionNameEditor from './ProjectionNameEditor/ProjectionNameEditor';
function Comments({ creator = false }) {
    const { t } = useTranslation(['specification', 'common']);
    const dispatch = useDispatch();
    const params = useParams();
    const navigate = useNavigate();
    const modals = useSelector(selectModals);
    const specificationId = useSelector(selectSpecificationId);
    const status = useSelector(selectSpecificationProjectionsStatus);
    const refetchFlag = useSelector(selectSpecificationProjectionsRefetchFlags);
    const projections = useSelector(selectSpecificationProjections);
    const activeProjection = useSelector(selectSpecificationProjection);
    const componentsStatus = useSelector(selectSpecificationComponentHeadersStatus);
    const componentsRefetch = useSelector(selectSpecificationComponentHeadersRefetch);
    const cachedSpecificationId = useSelector(selectSpecificationComponentHeadersSpecificationId);
    const [localProjectionId, setLocalProjectionId] = useState(null);
    useEffect(() => {
        if ((status.fetch === 'idle' || refetchFlag.fetch) && specificationId) {
            dispatch(fetchSpecificationProjections({ specificationId }));
        }
    }, [dispatch, status.fetch, refetchFlag.fetch, specificationId]);
    useEffect(() => {
        if (specificationId &&
            shouldFetchSpecificationComponentQuery(componentsStatus, componentsRefetch, cachedSpecificationId, specificationId)) {
            dispatch(fetchSpecificationComponentHeaders({ specificationId }));
        }
    }, [dispatch, componentsStatus, componentsRefetch, cachedSpecificationId, specificationId]);
    useEffect(() => {
        const projectionId = creator ? localProjectionId : params === null || params === void 0 ? void 0 : params.projectionId;
        if (projectionId) {
            dispatch(fetchSpecificationProjection({ specificationProjectionId: projectionId }));
        }
    }, [dispatch, params === null || params === void 0 ? void 0 : params.projectionId, localProjectionId, creator]);
    useEffect(() => {
        const currentProjectionId = creator ? localProjectionId : params === null || params === void 0 ? void 0 : params.projectionId;
        if (!currentProjectionId && status.fetch === 'succeeded' && projections.length > 0) {
            if (creator) {
                setLocalProjectionId(projections[0].id);
            }
            else {
                navigate(`/specifications/details/${specificationId}/comments/${projections[0].id}`, {
                    replace: true,
                });
            }
        }
    }, [
        params === null || params === void 0 ? void 0 : params.projectionId,
        localProjectionId,
        status.fetch,
        projections,
        navigate,
        specificationId,
        creator,
    ]);
    useEffect(() => {
        return () => {
            dispatch(resetSpecificationProjectionsState());
        };
    }, [dispatch]);
    const handleProjectionAdd = useCallback((chosenFile) => {
        if (!chosenFile || !specificationId)
            return;
        dispatch(addSpecificationProjection({
            specificationId,
            file: chosenFile,
        }));
    }, [dispatch, specificationId]);
    return (React.createElement(Grid, null,
        React.createElement(Header, { size: "m" }, t('comments')),
        React.createElement(VisualEditor, { menu: React.createElement(FileInput, { allowedExtensions: ['jpg', 'png', 'jpeg'], onSelect: handleProjectionAdd }, (openFileChooser) => (React.createElement("div", { style: {
                    overflow: 'hidden',
                    borderRadius: '8px',
                    background: 'var(--static-fill-foreground)',
                } }, projections.length > 0 ? (React.createElement(Dropdown, { size: "m", shape: "block", position: "bottomLeft", matchTriggerWidth: true, trigger: React.createElement(Button, { display: "block", theme: "border", rightIcon: React.createElement(ChevronDownSvg, null) }, activeProjection === null || activeProjection === void 0 ? void 0 : activeProjection.name) }, status.fetch === 'succeeded' && (React.createElement(React.Fragment, null,
                projections.map((projection) => (React.createElement("div", { key: projection.id, style: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'stretch',
                        gap: '4px',
                    } },
                    React.createElement(Button, { display: "block", theme: projection.id === (activeProjection === null || activeProjection === void 0 ? void 0 : activeProjection.id) ? 'fill' : 'ghost', to: creator
                            ? undefined
                            : `/specifications/details/${specificationId}/comments/${projection.id}`, rightIcon: React.createElement("div", { onClick: (e) => e.stopPropagation() },
                            React.createElement(Dropdown, { trigger: React.createElement(Button, { size: "xs", shape: "rounded", theme: "ghost", leftIcon: React.createElement(DotsIcon, null) }) },
                                React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(EditSvg, null), onClick: () => dispatch(openModal({
                                        name: 'projectionNameEditor',
                                        data: { id: projection.id, name: projection.name },
                                    })) }, t('common:change_name')),
                                React.createElement(Button, { align: "left", theme: "ghost", color: "danger", leftIcon: React.createElement(TrashSvg, null), onClick: () => dispatch(removeSpecificationProjection({
                                        projectionId: projection.id,
                                    })) }, t('common:delete')))), onClick: creator ? () => setLocalProjectionId(projection.id) : undefined }, projection.name)))),
                projections.length > 0 && React.createElement("hr", null),
                React.createElement(Button, { align: "left", theme: "ghost", leftIcon: React.createElement(PlusSvg, null), onClick: (e) => {
                        e.stopPropagation();
                        openFileChooser();
                    } }, t('add_projection')))))) : (React.createElement(Button, { align: "left", theme: "border", leftIcon: React.createElement(PlusSvg, null), onClick: (e) => {
                    e.stopPropagation();
                    openFileChooser();
                } }, t('add_projection')))))) }),
        (modals === null || modals === void 0 ? void 0 : modals.some((modal) => modal.name === 'projectionNameEditor')) && (React.createElement(ProjectionNameEditor, null)),
        (modals === null || modals === void 0 ? void 0 : modals.some((modal) => modal.name === 'commentCreator')) && React.createElement(CommentCreator, null),
        (modals === null || modals === void 0 ? void 0 : modals.some((modal) => modal.name === 'commentEditor')) && React.createElement(CommentCreator, { edit: true })));
}
export default Comments;
